import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type JSX,
} from "react";
import { App } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { pageMetaFor } from "../design/pageMeta";
import { feedbackApi, isCrmAuthError } from "./api";
import { IconPaperclip, IconSend, IconX } from "./icons";
import {
  FB_SEVERITY,
  FEEDBACK_FILES_MAX,
  FEEDBACK_FILE_MAX_MB,
  clientContext,
  crmSection,
  fileProblem,
  type FeedbackKind,
  type FeedbackSeverity,
} from "./model";
import {
  ROUTE_PREVIEW_MIN_CHARS,
  RoutingCard,
  useRoutePreview,
} from "./QuestionRouting";
import { previewFooterText, routingTextOf } from "./supportFormat";

/** Что отправили: код обращения и (для вопроса в УК) куда он ушёл — по ответу сервера, не по предпросмотру. */
export interface SentInfo {
  id: string;
  code: string;
  question?: {
    deptLabel: string;
    assigneeName: string | null;
    dueAt: string | null;
    urgent: boolean;
    slaMinutes?: number | null;
  };
}

const LABEL: Record<FeedbackKind, string> = {
  bug: "Что случилось",
  idea: "Идея",
  question: "Вопрос",
};
const PLACEHOLDER: Record<FeedbackKind, string> = {
  bug: "Например: кнопка «Запустить» крутится и ничего не происходит…",
  idea: "Например: показывать в кампании стоимость одного клиента",
  question: "Например: почему с баланса списали больше, чем я ожидал",
};
const FILE_LIMIT = `${String(FEEDBACK_FILE_MAX_MB).replace(".", ",")} МБ`;
const EMPTY_WARNING: Record<FeedbackKind, string> = {
  bug: "Опишите, что случилось",
  idea: "Опишите идею",
  question: "Напишите вопрос",
};

/** Миниатюра выбранного файла: картинка — превью, PDF — значок. */
function FileRow({ file, onRemove }: { file: File; onRemove: () => void }): JSX.Element {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="fk-fb-file">
      {src ? (
        <img src={src} alt="" className="fk-fb-file-thumb" />
      ) : (
        <IconPaperclip size={14} />
      )}
      <span className="fk-fb-file-name">{file.name}</span>
      <button
        type="button"
        className="fk-fb-file-x"
        aria-label="Убрать файл"
        onClick={onRemove}
      >
        <IconX size={12} />
      </button>
    </div>
  );
}

export function FeedbackForm({
  kind,
  roleLabel,
  full,
  sla,
  onSent,
  onAuthLost,
}: {
  kind: FeedbackKind;
  roleLabel: string | null;
  full: boolean;
  /** «Ответим в течение рабочего дня» — подпись у кнопки «Отправить». */
  sla: string;
  onSent: (r: SentInfo) => void;
  /** CRM ответила 401: нет входа в CRM — родитель покажет «Войдите в CRM». */
  onAuthLost: () => void;
}): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  // Страница, откуда пишут, — на момент открытия формы (виджет живёт поверх неё).
  const where = useMemo(
    () => ({ page: pageMetaFor(pathname).title, path: pathname }),
    [pathname],
  );

  const [body, setBody] = useState("");
  const [expected, setExpected] = useState("");
  const [sev, setSev] = useState<FeedbackSeverity>("annoy");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Вопрос в УК: отдел, выбранный вручную («Не туда?»), и предпросмотр «Кому уйдёт вопрос».
  const [manualDept, setManualDept] = useState<string | null>(null);
  const routed = kind === "question";
  const previewText = routed ? routingTextOf(null, body) : "";
  const preview = useRoutePreview(previewText, manualDept);
  // Текст стал короче порога — карточка «Кому уйдёт вопрос» исчезла вместе с кнопкой «Определить отдел автоматически»,
  // поэтому и ручной выбор сбрасываем: иначе он остался бы невидимым и ушёл бы с вопросом.
  useEffect(() => {
    if (manualDept && previewText.length < ROUTE_PREVIEW_MIN_CHARS)
      setManualDept(null);
  }, [manualDept, previewText]);

  const addFiles = (list: File[]) => {
    const ok: File[] = [];
    for (const f of list) {
      const problem = fileProblem(f);
      if (problem) message.warning(problem);
      else ok.push(f);
    }
    if (!ok.length) return;
    if (files.length + ok.length > FEEDBACK_FILES_MAX) {
      message.warning(`Можно приложить не больше ${FEEDBACK_FILES_MAX} файлов`);
    }
    setFiles((x) => [...x, ...ok].slice(0, FEEDBACK_FILES_MAX));
  };

  // Ctrl+V: картинка из буфера (скриншот) прикладывается как файл.
  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const imgs = Array.from(e.clipboardData?.files ?? []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (!imgs.length) return;
    addFiles(
      imgs.map((f, i) => {
        const ext = f.type === "image/jpeg" ? "jpg" : f.type === "image/webp" ? "webp" : "png";
        return new File([f], `снимок-экрана${i ? `-${i + 1}` : ""}.${ext}`, { type: f.type });
      }),
    );
  };

  const submit = async () => {
    if (!body.trim()) {
      message.warning(EMPTY_WARNING[kind]);
      return;
    }
    setBusy(true);
    try {
      const section = crmSection(where.page);
      const r = await feedbackApi.create({
        kind,
        body: body.trim(),
        expected: kind === "bug" ? expected.trim() || undefined : undefined,
        severity: kind === "bug" ? sev : undefined,
        section,
        pageUrl: `${window.location.origin}${where.path}`,
        context: clientContext(section, roleLabel),
        // Отдел уходит, только если ручной выбор виден в карточке (предпросмотр ответил routedBy: 'manual');
        // иначе его определит бэкенд по тому же тексту, что и предпросмотр.
        deptCode:
          routed && manualDept && preview.result?.routedBy === "manual"
            ? manualDept
            : undefined,
      });
      // Вложения — после создания: обращение уже есть, даже если файл не дошёл.
      let failed = 0;
      for (const f of files) {
        await feedbackApi
          .attach(
            r.id,
            f,
            f.name,
            kind === "bug" && f.type.startsWith("image/") ? "screenshot" : "file",
          )
          .catch(() => (failed += 1));
      }
      if (failed)
        message.warning(`Обращение отправлено, но не приложилось файлов: ${failed}`);
      void qc.invalidateQueries({ queryKey: ["feedback"] });
      // Куда ушёл вопрос — по данным сервера (он мог определить отдел чуть иначе, чем предпросмотр).
      let question: SentInfo["question"];
      if (routed) {
        const d = await feedbackApi.myOne(r.id).catch(() => null);
        if (d?.deptLabel) {
          // Срок в минутах знает только предпросмотр — берём его, если отдел тот же («в течение часа» у срочных).
          const slaMinutes =
            preview.result?.deptLabel === d.deptLabel ? preview.result.slaMinutes : null;
          question = {
            deptLabel: d.deptLabel,
            assigneeName: d.assigneeName ?? null,
            dueAt: d.dueAt ?? null,
            urgent: !!d.urgent,
            slaMinutes,
          };
        }
      }
      onSent({ ...r, question });
    } catch (e) {
      // Нет входа в CRM — не «ошибка», а просьба войти; текст остаётся в форме.
      if (isCrmAuthError(e)) onAuthLost();
      else message.error((e as Error).message || "Не удалось отправить обращение");
    } finally {
      setBusy(false);
    }
  };

  const atLimit = files.length >= FEEDBACK_FILES_MAX;
  return (
    <div className="fk-fb-form" onPaste={onPaste}>
      <div className="fk-fb-cols" data-full={full ? "true" : "false"}>
        <div>
          <div className="fk-fb-label">{LABEL[kind]}</div>
          <textarea
            autoFocus
            className="fk-fb-input fk-fb-textarea"
            data-full={full ? "true" : "false"}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={PLACEHOLDER[kind]}
            maxLength={5000}
          />
          {routed && (preview.result || preview.loading) ? (
            <div className="fk-fb-gap">
              <RoutingCard
                preview={preview}
                manualDept={manualDept}
                onPickDept={setManualDept}
                onResetDept={() => setManualDept(null)}
              />
            </div>
          ) : null}
          {kind === "bug" ? (
            <>
              <div className="fk-fb-gap">
                <div className="fk-fb-label">Что ожидали</div>
                <textarea
                  className="fk-fb-input fk-fb-textarea fk-fb-textarea--sm"
                  data-full={full ? "true" : "false"}
                  value={expected}
                  onChange={(e) => setExpected(e.target.value)}
                  placeholder="Кампания запустится и появится в списке"
                  maxLength={3000}
                />
              </div>
              <div className="fk-fb-gap">
                <div className="fk-fb-label">Насколько мешает</div>
                <div className="fk-fb-sev">
                  {(Object.keys(FB_SEVERITY) as FeedbackSeverity[]).map((k) => (
                    <button
                      type="button"
                      key={k}
                      className="fk-fb-sev-btn"
                      aria-pressed={sev === k}
                      onClick={() => setSev(k)}
                    >
                      <span className="fk-fb-sev-name">
                        <span
                          className="fk-fb-sev-dot"
                          style={{ background: FB_SEVERITY[k].tone }}
                        />
                        {FB_SEVERITY[k].label}
                      </span>
                      <span className="fk-fb-sev-hint">{FB_SEVERITY[k].hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div>
          {kind === "bug" ? <div className="fk-fb-label">Снимок экрана</div> : null}
          <button
            type="button"
            className="fk-fb-tool"
            onClick={() => fileRef.current?.click()}
            disabled={atLimit}
          >
            <IconPaperclip size={13} />
            {kind === "bug" ? "Загрузить снимок или файл" : "Приложить картинку или PDF"}
          </button>
          <div className="fk-fb-hint">
            {kind === "bug"
              ? `Можно вставить снимок из буфера: Ctrl+V. PNG, JPG, WEBP или PDF до ${FILE_LIMIT}.`
              : `PNG, JPG, WEBP или PDF до ${FILE_LIMIT}. Картинку можно вставить из буфера: Ctrl+V.`}
          </div>
          {files.length ? (
            <div className="fk-fb-files">
              {files.map((f, i) => (
                <FileRow
                  key={`${f.name}-${i}`}
                  file={f}
                  onRemove={() => setFiles((x) => x.filter((_, j) => j !== i))}
                />
              ))}
            </div>
          ) : null}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            multiple
            hidden
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
        </div>
      </div>

      <div className="fk-fb-foot">
        <button
          type="button"
          className="fk-fb-send"
          disabled={busy}
          onClick={() => void submit()}
        >
          <IconSend size={14} />
          {busy ? "Отправляем…" : "Отправить"}
        </button>
        <span className="fk-fb-foot-note">
          {routed && preview.result ? previewFooterText(preview.result) : sla}
        </span>
      </div>
    </div>
  );
}
